import { type ITemplateVariableSource } from "commonComponents/btWrappers/editor/core/extensions/custom/templateVariable/TemplateVariableExtension";

/** Shown for Total Paid on Job before any payment has been sent to the sub on the job. */
export const TOTAL_PAID_ON_JOB_EMPTY_FALLBACK = "Will display amount when sent";

// Must be a `type`, not an `interface`: it's used as the generic arg to `ITemplateVariableSource<T>`,
// whose constraint `T extends Record<string, string | number | Date | undefined>` an open interface
// can't satisfy without a lossy `[key: string]` index signature. See TemplateVariableExtension.ts.
//
// `effectiveDate` and `paymentAmount` accept either a raw `Date`/`number` — which the chip formats
// locale-aware via DateDisplay/CurrencyDisplay — or a `string`, which is rendered verbatim. The
// string form carries pre-resolved fallbacks (e.g. "Will display amount when payment is sent" or the
// cost-view-permission gate) that the client supplies; the server sends raw values.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type ILienWaiverTemplateVariables = {
    subcontractor: string;
    jobAddress: string;
    effectiveDate: Date | string;
    paymentAmount: number | string;
    totalPaidOnJob: number | string;
    lotParcelInfo?: string;
};

/**
 * The stamped variables as they arrive on the wire. The server sends raw values so the client
 * formats them: `paymentAmount` is a raw number — or `null` when the requesting user can't view cost,
 * in which case the client substitutes the permission-gate fallback text. The effective date is not
 * stamped on the wire; each caller derives it (e.g. from the approval date) and passes it in.
 */
export interface ILienWaiverStampedVariablesApi
    extends Omit<
        ILienWaiverTemplateVariables,
        "effectiveDate" | "paymentAmount" | "totalPaidOnJob"
    > {
    paymentAmount?: number | null;
    totalPaidOnJob?: number | null;
}

/**
 * Parses the stamped variables from an API response into the shape the editor resolves against:
 *  - `effectiveDate` — derived by the caller from its own source (e.g. the approval date); empty until
 *    the waiver is approved;
 *  - `paymentAmount` → the raw number (CurrencyDisplay formats it) when the user can view cost and an
 *    amount exists; the permission-gate fallback when they can't (the server nulls the amount in that
 *    case); or the "will display when sent" fallback when there's simply no payment yet.
 *
 * `canViewPrice` comes from the parent response — the server nulls the amount for a user without
 * cost-view permission, so the client relies on the flag to pick the right fallback text.
 *
 * `effectiveDate` is always supplied by the caller (the server no longer stamps it on the wire).
 */
export function parseLienWaiverStampedVariables(
    data: ILienWaiverStampedVariablesApi | undefined | null,
    canViewPrice: boolean,
    effectiveDate: Date | string = "",
): ILienWaiverTemplateVariables | undefined {
    if (!data) {
        return undefined;
    }
    const { paymentAmount, totalPaidOnJob, ...rest } = data;

    return {
        ...rest,
        effectiveDate,
        paymentAmount: resolveStampedAmount(
            paymentAmount,
            canViewPrice,
            "Will display amount when payment is sent",
        ),
        totalPaidOnJob: resolveStampedAmount(
            totalPaidOnJob,
            canViewPrice,
            TOTAL_PAID_ON_JOB_EMPTY_FALLBACK,
        ),
    };
}

/**
 * Resolves a stamped currency amount to what the chip renders: the raw number (CurrencyDisplay formats
 * it) when the user can view cost and an amount exists; the permission-gate fallback when they can't
 * (the server nulls the amount in that case); or `emptyFallback` when there's no amount yet.
 */
function resolveStampedAmount(
    amount: number | null | undefined,
    canViewPrice: boolean,
    emptyFallback: string,
): number | string {
    if (!canViewPrice) {
        return "Cost viewing permission required";
    }
    if (amount != null) {
        return amount;
    }
    return emptyFallback;
}

/** Builds the lien-waiver variable source for the editor's `templateVariable.sources` config, resolving against the supplied values. */
export function getLienWaiverVariableSource(
    values?: ILienWaiverTemplateVariables,
): ITemplateVariableSource<ILienWaiverTemplateVariables> {
    return {
        namespace: "lienWaiver",
        label: "Lien waiver",
        definitions: {
            subcontractor: "Subcontractor",
            jobAddress: "Job Address",
            effectiveDate: { label: "Effective Date", type: "date" },
            paymentAmount: { label: "Payment Amount", type: "currency" },
            totalPaidOnJob: { label: "Total Paid on Job", type: "currency" },
            lotParcelInfo: "Lot/Parcel Info",
        },
        values,
    };
}
